import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { localizedProps, pick } from "src/utils/localize";
import { tourDetails } from "src/utils/whatsapp";
import CtaButton from "../../components/cta-button";
import Info from "../../components/info";
import { Description } from "./components/description";
import DepositBlock from "./components/deposit-block";
import IncludedLists from "./components/included-lists";
import TourExtras, { extrasSummaryText, useTourExtras } from "./components/tour-extras";
import IncludedServices from "./components/included-services";
import Itinerary from "./components/itinerary";
import StickyBookBar from "./components/sticky-book-bar";
import TourRoute from "./components/tour-route";
import TourReviews from "./components/tour-reviews";
import AutoGallery, { tourImages } from "../../components/auto-gallery";

export default function ActivityInfo({ activity }) {
  const { t, i18n } = useTranslation();
  const selectedLanguage = i18n.language;

  // якорь для липкой полосы: пока эта кнопка на экране, полосы нет
  const ctaRef = useRef(null);

  const name = localizedProps(activity?.name, selectedLanguage);
  const place = pick(activity?.place, selectedLanguage);
  const category = pick(activity?.category, selectedLanguage);
  // «Место / Категория» — разделитель нужен только когда есть обе части,
  // иначе строка начиналась или заканчивалась висящим слэшем
  const eyebrow = [place, category].filter(Boolean).join(" / ");
  const priceNote = pick(activity?.priceNote, selectedLanguage);
  const descriptions = activity?.descriptions || [];

  // КАЛЬКУЛЯТОР ОПЦИЙ. Состояние держим здесь, а не внутри блока, потому что
  // выбранное нужно двум местам сразу: самому блоку и кнопке заявки —
  // список опций и итоговая сумма уходят менеджеру вместе с обращением.
  const extras = useTourExtras(activity?.extras, activity?.price, selectedLanguage);
  // Даты, маршрут, цена и взнос — они уходят в WhatsApp вместе с названием
  // тура, чтобы менеджеру не пришлось переспрашивать «а это какой заезд?».
  const details = tourDetails(activity, { t, lang: selectedLanguage });
  const extrasComment = extrasSummaryText(extras, {
    title: t("tour.extrasTitle"),
    total: t("tour.extrasTotal"),
  });

  return (
    <>
      <div className="grid items-start grid-cols-1 gap-y-10 sm:gap-y-14 gap-x-8 lg:grid-cols-2">
        <div className="order-2 lg:order-1">
          {eyebrow && (
            <p className="font-bold uppercase tracking-[0.1em] text-xs sm:text-sm text-kj-gold">
              {eyebrow}
            </p>
          )}
          <h1
            {...name.attrs}
            className="mt-3 text-2xl sm:text-4xl font-extrabold uppercase tracking-tight text-kj-ink text-balance"
          >
            {name.value}
          </h1>

          <TourRoute route={activity?.route} className="mt-5" />

          <Info
            price={activity?.price}
            priceNote={priceNote}
            date={activity?.date}
            dateEnd={activity?.dateEnd}
          />

          <DepositBlock
            deposit={activity?.deposit}
            depositNote={activity?.depositNote}
            className="mt-5"
          />

          <IncludedServices activity={activity} />

          <div ref={ctaRef} className="mt-8">
            {/* Кнопка уводит в WhatsApp с готовым сообщением о туре */}
            <CtaButton
              item={activity}
              subject={name.value}
              details={details}
              intent="book"
              fallbackLabel={t("request.book")}
              presetComment={extrasComment}
              className="w-full sm:w-auto"
            />
          </div>
        </div>

        <div className="order-1 lg:order-2">
          <AutoGallery
            images={tourImages(activity)}
            alt={name.value}
            interval={4500}
            heightClass="aspect-square"
            rounded="rounded-lg"
            fallback={
              // Пока фото тура не загружены, здесь стоял пустой тёмный
              // квадрат во весь столбец — на странице тура за 2600 $ он
              // выглядел как не прогрузившаяся картинка. Тот же приём, что
              // на карточках: градиент палитры плюс сэйгайха.
              <div
                style={{
                  "--pattern-alpha": 0.16,
                  background:
                    "linear-gradient(145deg, rgb(var(--kj-gold) / 0.3), rgb(var(--kj-line)))",
                }}
                className="aspect-square rounded-lg border border-kj-line overflow-hidden flex items-center justify-center px-8 pattern-seigaiha"
              >
                <span className="text-center text-kj-ink/80 text-sm font-bold uppercase tracking-[0.14em] text-balance">
                  {name.value || "Kazakh Japan"}
                </span>
              </div>
            }
          />
        </div>
      </div>

      {/* Длинные блоки идут во всю ширину: программа на 8–11 дней и списки
          «включено / не включено» в узкой колонке нечитаемы. */}
      <Itinerary itinerary={activity?.itinerary} className="mt-2" />

      <IncludedLists
        included={activity?.included}
        notIncluded={activity?.notIncluded}
      />

      {/* «Собери свой тур»: галочки опций и живой пересчёт стоимости.
          Стоит сразу после «включено / не включено» — человек только что
          прочитал, чего в цене НЕТ, и тут же может это добавить. */}
      <TourExtras state={extras} />

      {descriptions.length > 0 && (
        <dl className="flex flex-col gap-4">
          {descriptions.map((feature, i) => (
            <Description
              key={feature?.name?.en || i}
              feature={feature}
              selectedLanguage={selectedLanguage}
            />
          ))}
        </dl>
      )}

      {/* ЗАКРЫВАЮЩИЙ ПРИЗЫВ. Наверху страницы кнопка стоит рядом с ценой —
          для тех, кто уже решился. Но человек, который дочитал программу по
          дням, «включено / не включено» и опции, оказывался в конце страницы
          без единой кнопки: липкая полоса на компьютере не показывается, и
          приходилось листать обратно наверх. Здесь тот же CtaButton — с тем же
          сообщением и теми же выбранными опциями. */}
      <section className="relative overflow-hidden w-full rounded-2xl border border-kj-line bg-kj-card p-6 sm:p-10 flex flex-col items-start gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl sm:text-2xl font-extrabold uppercase tracking-tight text-kj-ink">
            {t("tour.closingTitle")}
          </h2>
          <p className="mt-2 text-base text-kj-sub max-w-xl leading-relaxed">
            {t("tour.closingText")}
          </p>
        </div>
        <CtaButton
          item={activity}
          subject={name.value}
          details={details}
          intent="book"
          fallbackLabel={t("request.book")}
          presetComment={extrasComment}
          className="w-full sm:w-auto shrink-0"
        />
      </section>

      {/* Чужой опыт — последнее, что человек читает перед решением. Стоит
          после закрывающего призыва: сначала кнопка для тех, кто уже решился,
          потом доводы для тех, кто ещё сомневается. */}
      <TourReviews activityId={activity?._id} />

      <StickyBookBar
        anchorRef={ctaRef}
        subject={name.value}
        price={extras.totalText || activity?.price}
        details={details}
        presetComment={extrasComment}
      />
    </>
  );
}
